import type { ReactNode } from "react";
import { Box, Typography, Paper } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { APP_TITLE, Routes } from "../constants";
import { getApiDocsHref } from "../api";

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        mb: 3,
        borderRadius: 2,
        border: 1,
        borderColor: "divider",
      }}
    >
      <Typography
        variant="h2"
        sx={{ fontSize: "1.35rem", fontWeight: 700, mb: 2 }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          "& p": { mb: 1.5, color: "text.secondary", lineHeight: 1.65 },
          "& p:last-child": { mb: 0 },
          "& em": { fontStyle: "italic" },
          "& code": { fontSize: "0.92em" },
          "& a": { color: "primary.main" },
        }}
      >
        {children}
      </Box>
    </Paper>
  );
}

export function InfoPage() {
  return (
    <Box>
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Typography
          variant="h1"
          sx={{ fontSize: { xs: "1.65rem", sm: "2rem" }, mb: 1 }}
        >
          Informacije o rješenju
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ maxWidth: 840, "& em": { fontStyle: "italic" } }}
        >
          {APP_TITLE}: centraliziran pregled podataka sportskih saveza Hrvatske,
          automatizirano povezanih s javnim izvorima.
        </Typography>
      </Box>

      <Section title="Zašto postoji aplikacija?">
        <Typography variant="body1" component="p">
          Rješenje nastaje iz potrebe za jedinstvenim i strukturiranim pregledom
          podataka rasutih po pojedinim <em>web</em> stranicama saveza - od
          kontakata i vijesti do dokumenata, članstva, rasporeda i ostalog - bez
          ručnoga kopanja po svakoj organizaciji zasebno.
        </Typography>
        <Typography variant="body1" component="p">
          Korijeni su u <em>hackathonu</em> kojeg je organizirao ZICER, u okviru
          inovacija u sportu: to je konkretan tehnički pothvat da javno dostupni
          sportski podaci, po uzoru na{" "}
          <a
            href="https://data.zagreb.hr/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Zagreb Open Data Portal
          </a>
          , budu skupno strukturirani i korisnicima dohvatljivi na jednome
          mjestu.
        </Typography>
      </Section>

      <Section title="Što se događa u pozadini?">
        <Typography variant="body1" component="p">
          U pozadini radi pozadinski servis (<em>worker</em>) koji pokreće
          <em>zadatke</em> za pojedini sportski savez: zasebni moduli posjećuju
          službene stranice saveza te iz oznaka <em>HTML</em>a ili drugih izvora
          izdvajaju polja u jedinstveni strukturirani model (detalji, adresa,
          kontakt, vijesti, članovi, dokumenti, pravilnici, rasporedi,
          reference, tekstovi &bdquo;o nama&ldquo; te ostalo, primjerice OIB ili
          IBAN ako su dostupni) u najboljem mogućem obliku za daljnju upotrebu.
          Naravno, uvijek postoje iznimke, greške, promjene izvornih stranica,
          koje utječu na kvalitetu i količinu dostupnih podataka.
        </Typography>
        <Typography variant="body1" component="p">
          Prikupljeni rezultati spremaju se u bazu podataka. Ti su zapisi
          dostupni aplikaciji i vanjskim sustavima preko sučelja tipa{" "}
          <em>REST API</em>. <em>Web</em>-sučelje koristi istu strukturu koja je
          opisana u odjeljku <em>API</em>.
        </Typography>
      </Section>

      <Section title="Što točno pokazuje stranica &bdquo;Početna&ldquo;?">
        <Typography variant="body1" component="p">
          <strong>
            <em>Hero</em> blok
          </strong>
          : naslov aplikacije, kratak uvodni opis te kartice s prikazom brojki -
          koliko je saveza u registru (trenutačno skup isključivo saveza s
          područja Grada Zagreba), vrijeme zadnjeg ažuriranja podataka, postotak
          saveza koji imaju osnovne podatke te ukupan broj prikupljenih stavki u
          rubrikama vijesti, članstva i dokumenata. Ispod toga nalazi se glavni
          gumb koji vodi na pretragu saveza.
        </Typography>
        <Typography variant="body1" component="p">
          <strong>Statistički pregled sustava</strong>: u tabličnom prikazu
          vidite koliko je saveza ispunilo pojedino područje podataka (vijesti,
          članstvo, dokumenti i ostali stupci). Jasno se vidi i koliko ih za
          određenu vrstu podataka još nema nikakvih unosa na razini cijelog
          registra.
        </Typography>
        <Typography variant="body1" component="p">
          <strong>Pokrivenost po savezima</strong>: jedan je red za pojedini
          savez, a stupci su po modulima koje sustav prati. Na taj način vrlo
          brzo i jednostavno možete uočiti ima li određeni savez ipak neki trag
          integracije u pojedinim kategorijama.
        </Typography>
      </Section>

      <Section title="Što prikazuje stranica &bdquo;Greške&ldquo;?">
        <Typography variant="body1" component="p">
          Stranica{" "}
          <RouterLink to={Routes.ERRORS}>&bdquo;Greške&ldquo;</RouterLink> služi
          za pregled problema koji se dogode tijekom automatskog prikupljanja
          podataka. Kad <em>worker</em> ne uspije dohvatiti stranicu, obraditi
          dio sadržaja ili naiđe na neočekivanu promjenu izvora, sustav to
          zabilježi kao događaj s vremenom, porukom i dijagnostičkim detaljima
          (npr. koji je modul i korak pao).
        </Typography>
        <Typography variant="body1" component="p">
          Na početku vidite sažetak po savezima: koliko je grešaka zabilježeno u
          zadnjih <strong>14 dana</strong>, kada je zadnja nastala i kratki
          opis. Odabirom saveza otvara se popis pojedinačnih zapisa. Greške koje
          nisu vezane uz konkretan savez (npr. problemi na razini sustava)
          prikazuju se pod stavkom <strong>Sustav</strong>.
        </Typography>
        <Typography variant="body1" component="p">
          Prazan popis ne znači nužno da je sve savršeno - samo da u tom
          razdoblju nije bilo zabilježenih neuspjeha. Obratno, pojedina greška
          ne mora značiti da su svi podaci saveza nedostupni; često je riječ o
          privremenom kvaru mreže ili jednom neuspjelom koraku dok je ostatak
          prikupljanja ipak uspio.
        </Typography>
      </Section>

      <Section title="Što prikazuje stranica &bdquo;Status&ldquo;?">
        <Typography variant="body1" component="p">
          Stranica{" "}
          <RouterLink to={Routes.STATUS}>&bdquo;Status&ldquo;</RouterLink>{" "}
          pokazuje <strong>dostupnost</strong> sustava i izvornih web stranica
          saveza tijekom zadnjih <strong>14 dana</strong>. To je odvojeno od
          stranice &bdquo;Greške&ldquo;: ovdje se ne bilježe greške pri obradi
          sadržaja, nego jesu li odredišta uopće dostupna putem <em>HTTP</em>{" "}
          zahtjeva.
        </Typography>
        <Typography variant="body1" component="p">
          Gornji dio prikazuje dostupnost same <em>REST API</em> instance
          (provjera zdravlja servisa). Ispod slijedi dnevni grafikon, a zatim
          kartice pojedinih saveza čije se službene stranice periodično
          provjeravaju. Za svaki savez vidite postotak uspješnih odgovora,
          prosječno vrijeme odziva i kratki status (npr. operativan, smanjena
          dostupnost ili ozbiljniji prekid).
        </Typography>
        <Typography variant="body1" component="p">
          Status ne govori je li sadržaj na stranici saveza točan ili potpun -
          samo je li stranica u tom trenutku bila dohvatljiva. Korisno je za
          brzu procjenu radi li izvorna stranica i je li API dostupan
          korisnicima i vanjskim sustavima.
        </Typography>
      </Section>

      <Section title="Kako često se osvježavaju podaci?">
        <Typography variant="body1" component="p">
          <em>Worker</em> raspoređuje <em>zadatke</em> u ciklusima: cijeli
          obradni ciklus ponavlja se otprilike svaka <strong>tri sata</strong> (
          <code style={{ fontSize: "0.92em" }}>3&nbsp;h</code>). Unutar toga
          savezi se obrađuju u manjim skupinama, s kratkim pauzama između
          skupina kako istodobno ne bi svi opterećivali izvorne stranice.
        </Typography>
        <Typography variant="body1" component="p">
          Za korisnika to znači: detaljni prikazi i preuzimanja u obliku{" "}
          <em>JSON</em> odražavaju stanje nakon posljednje uspješne
          sinkronizacije. Ako savez još nije obrađen ili je izvorna stranica
          promijenjena, pojedino polje privremeno može nedostajati dok ga
          <em>worker</em> opet ne uspije učitati, ili dok sustav ne dobije
          ažuriranje i unaprjeđivanje procesa dohvata podataka.
        </Typography>
      </Section>

      <Section title="Daljnji koraci">
        <Typography variant="body1" component="p">
          Na stranici pojedinog saveza možete preuzeti cijeli zapis kao{" "}
          <em>JSON</em> - isti oblik koji <em>API</em> vraća za taj dokument u
          bazi - primjerice radi analize, arhiviranja ili korištenja u
          izrađivanju vlastitih alata.
        </Typography>
      </Section>
    </Box>
  );
}
